import { Link } from 'react-router-dom';
import { User, Star, MapPin, Settings, LogOut, ChevronRight, Utensils, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth-store';
import { formatBRL } from '@rango/shared/utils';

export function ProfilePage() {
  const { user, logout } = useAuthStore();
  
  const handleLogout = () => {
    logout();
    window.location.href = '/bem-vindo';
  };
  
  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="bg-gradient-to-br from-rango-500 to-rango-600 px-4 pb-20 pt-8 text-white">
        <div className="mx-auto max-w-lg">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-2xl font-bold">Meu Perfil</h1>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Profile card */}
      <div className="mx-auto -mt-16 max-w-lg px-4">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  <User className="h-10 w-10 text-muted-foreground" />
                )}
              </div>
              
              <div className="flex-1">
                <h2 className="font-display text-xl font-semibold">{user?.name}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                
                {user?.location && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {user.location.city}, {user.location.state}
                  </p>
                )}
              </div>
            </div>
            
            {/* Ratings */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold">
                    {user?.ratings.asHost.average.toFixed(1) || '—'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  como anfitrião ({user?.ratings.asHost.count || 0})
                </p>
              </div>
              
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold">
                    {user?.ratings.asGuest.average.toFixed(1) || '—'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  como convidado ({user?.ratings.asGuest.count || 0})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Stats */}
      <div className="mx-auto mt-6 max-w-lg px-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="font-display text-2xl font-bold text-primary">
                  {user?.balances.tokens.toLocaleString('pt-BR')}
                </p>
                <p className="text-sm text-muted-foreground">Tokens</p>
              </div>
              <div className="text-center">
                <p className="font-display text-2xl font-bold text-emerald-600">
                  {formatBRL(user?.balances.credits || 0)}
                </p>
                <p className="text-sm text-muted-foreground">Créditos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Menu */}
      <div className="mx-auto mt-6 max-w-lg px-4">
        <Card className="border-0 shadow-md">
          <CardContent className="divide-y p-0">
            <Link
              to="/minhas-listagens"
              className="flex items-center justify-between p-4 hover:bg-muted/50"
            >
              <div className="flex items-center gap-3">
                <Utensils className="h-5 w-5 text-muted-foreground" />
                <span>Minhas listagens</span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Link>
            
            <Link
              to="/minhas-reservas"
              className="flex items-center justify-between p-4 hover:bg-muted/50"
            >
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <span>Minhas reservas</span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Link>
            
            <button
              onClick={handleLogout}
              className="flex w-full items-center justify-between p-4 text-destructive hover:bg-destructive/5"
            >
              <div className="flex items-center gap-3">
                <LogOut className="h-5 w-5" />
                <span>Sair</span>
              </div>
            </button>
          </CardContent>
        </Card>
      </div>
      
      {/* KYC Status */}
      {user?.kycStatus === 'verified' && (
        <div className="mx-auto mt-6 max-w-lg px-4">
          <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            Identidade verificada
          </div>
        </div>
      )}
    </div>
  );
}

